import React from "react";
import { FormattedMessage } from "react-intl";
import { useQueryData, useToast } from "hooks";
import { GET_ASSESSMENT } from "graphql/queries";
import { useMutation } from "@apollo/client";
import {
  MOVE_ASSESSMENT_QUESTION,
  REMOVE_ASSESSMENT_QUESTION,
  UPDATE_MISSION_ROLE_ASSESSMENT_QUESTION,
} from "graphql/mutations";
import { buildOrderedRowOptions } from "utils";
import { ICON_TYPE } from "components/Icon";
import Tooltip from "components/Tooltip";

export default function useAssessment({ id, resourceType, resourceId, allowedId, skip, skipAnswers = false }) {
  const keyName = "assessment";
  const { toast } = useToast();
  const { loading, data, error, refetch } = useQueryData({
    queryName: GET_ASSESSMENT,
    keyName,
    skip: !id || skip,
    variables: {
      id,
      allowedId,
      resourceType,
      resourceId,
      skipAnswers,
    },
  });

  const [removeQuestion, { loading: removingQuestion }] = useMutation(REMOVE_ASSESSMENT_QUESTION);

  const [updateQuestion, { loading: updatingQuestion }] = useMutation(UPDATE_MISSION_ROLE_ASSESSMENT_QUESTION, {
    refetchQueries: [GET_ASSESSMENT],
  });

  const [moveQuestion, { loading: movingQuestion }] = useMutation(MOVE_ASSESSMENT_QUESTION);

  const handleRemoveQuestion = (questionId) => {
    removeQuestion({
      variables: {
        input: {
          questionId,
        },
      },
      onCompleted: () => {
        toast.success(<FormattedMessage id="useAssessment.RemoveQuestionSuccess" />);
        refetch();
      },
      onError: (error) => {
        const hasAnswersError = error?.graphQLErrors?.some(
          (graphQLError) => graphQLError?.extensions?.code === "QUESTION_HAS_ANSWERS"
        );

        toast.error(
          <FormattedMessage
            id={hasAnswersError ? "useAssessment.RemoveQuestionHasAnswersError" : "useAssessment.RemoveQuestionError"}
          />
        );
      },
    });
  };

  const handleToggleQuestion = ({ wording, id, properties, type: answerType } = {}, assessmentId) =>
    updateQuestion({
      variables: {
        input: {
          id,
          params: {
            assessmentId,
            wording,
            answerType,
            properties: {
              enabled: !properties?.enabled,
            },
          },
        },
      },
      onCompleted: () => toast.success(<FormattedMessage id="useAssessment.ToggleQuestionSuccess" />),
      onError: () => toast.error(<FormattedMessage id="useAssessment.ToggleQuestionError" />),
    });

  const handleMoveQuestion = (questionId, direction, refetchQueries = []) => {
    moveQuestion({
      variables: {
        input: {
          questionId,
          direction,
        },
      },
      refetchQueries,
      onCompleted: () => toast.success(<FormattedMessage id="Missions.Roles.QuestionsList.Question.MoveSuccess" />),
      onError: () => toast.error(<FormattedMessage id="Missions.Roles.QuestionsList.Question.MoveError" />),
    });
  };

  const getRowOptions = ({ question, assessment, handleEdit }) => {
    const hasResponses = question?.responsesCount > 0;

    const buildLabel = (labelId) =>
      hasResponses ? (
        <Tooltip tooltipId="useAssessment.QuestionHasResponsesTooltip">
          <FormattedMessage id={labelId} />
        </Tooltip>
      ) : (
        <FormattedMessage id={labelId} />
      );

    return [
      {
        label: buildLabel("Global.Edit"),
        icon: ICON_TYPE.edit,
        disabled: hasResponses,
        onClick: () => handleEdit(question),
      },
      {
        label: buildLabel("Global.Remove"),
        icon: ICON_TYPE.trash,
        disabled: hasResponses,
        onClick: () => handleRemoveQuestion(question.id),
      },
      ...buildOrderedRowOptions({
        item: question,
        items: assessment?.questions || [],
        onMove: (id, direction) => handleMoveQuestion(id, direction, [GET_ASSESSMENT]),
      }),
    ];
  };

  return {
    data: data?.[keyName],
    loading,
    error,
    refetch,
    handleRemoveQuestion,
    handleToggleQuestion,
    removingQuestion,
    updatingQuestion,
    getRowOptions,
    movingQuestion,
  };
}
